(() => {
  if (customElements.get('product-size-guide')) return;

  const object = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
  const text = (value) => typeof value === 'string' && value.length > 0;
  const strings = (value) => Array.isArray(value) && value.every(text);
  const exactKeys = (value, required, optional = []) =>
    object(value) && required.every((key) => Object.hasOwn(value, key)) &&
    Object.keys(value).every((key) => required.includes(key) || optional.includes(key));
  const sha256 = (value) => typeof value === 'string' && /^[a-f0-9]{64}$/.test(value);
  const optionIdentity = (value) => exactKeys(value, ['position', 'name']) &&
    Number.isInteger(value.position) && value.position >= 1 && value.position <= 3 && text(value.name);
  const chartKeys = ['id', 'title', 'review', 'review_record', 'sources', 'applicability', 'garment_type',
    'garment_evidence', 'columns', 'column_specs', 'rows', 'size_aliases', 'notes', 'warnings', 'diagram'];

  function validSource(source) {
    if (!exactKeys(source, ['kind', 'reference', 'sha256', 'audit_record'], ['url', 'crop', 'source_caption'])) return false;
    if (!['image', 'description', 'existing_metafield'].includes(source.kind) || !text(source.reference) ||
      !sha256(source.sha256) || !text(source.audit_record)) return false;
    if (Object.hasOwn(source, 'url')) {
      try { new URL(source.url); } catch { return false; }
    }
    if (Object.hasOwn(source, 'source_caption') && typeof source.source_caption !== 'string') return false;
    if (Object.hasOwn(source, 'crop')) {
      const crop = source.crop;
      if (!Array.isArray(crop) || crop.length !== 4 || !crop.every((n) => Number.isFinite(n) && n >= 0 && n <= 1) ||
        crop[0] + crop[2] > 1 || crop[1] + crop[3] > 1) return false;
    }
    return true;
  }

  function validApplicability(scope) {
    if (scope?.mode === 'product') return exactKeys(scope, ['mode']);
    return exactKeys(scope, ['mode', 'constraints']) && scope.mode === 'option_values' &&
      Array.isArray(scope.constraints) && scope.constraints.length > 0 && scope.constraints.every((constraint) =>
        exactKeys(constraint, ['position', 'name', 'values']) &&
        optionIdentity({ position: constraint.position, name: constraint.name }) &&
        strings(constraint.values) && constraint.values.length > 0 &&
        new Set(constraint.values).size === constraint.values.length);
  }

  function validChart(chart) {
    if (!exactKeys(chart, chartKeys) || chart.review !== 'approved' || !text(chart.id) || !text(chart.title) ||
      !text(chart.review_record) || !Array.isArray(chart.sources) || !chart.sources.length ||
      !chart.sources.every(validSource) || !validApplicability(chart.applicability)) return false;
    if (!(chart.garment_type === null || text(chart.garment_type)) ||
      !(chart.garment_evidence === null || typeof chart.garment_evidence === 'string') ||
      typeof chart.notes !== 'string' || !strings(chart.warnings) ||
      ![null, 'tshirt', 'longsleeve', 'pants', 'shorts', 'jacket', 'vest'].includes(chart.diagram)) return false;
    if (!strings(chart.columns) || !chart.columns.length || !Array.isArray(chart.column_specs) ||
      chart.column_specs.length !== chart.columns.length || !chart.column_specs.every((spec) =>
        exactKeys(spec, ['source_label', 'quantity', 'basis', 'unit', 'diagram_label']) &&
        typeof spec.source_label === 'string' &&
        ['chest', 'waist', 'hip', 'length', 'outer_length', 'inseam', 'sleeve', 'shoulders', 'size_system', 'other', 'unknown'].includes(spec.quantity) &&
        ['flat_width', 'circumference', 'linear', 'body_measurement', 'size_system', 'unknown'].includes(spec.basis) &&
        (spec.unit === null || text(spec.unit)) && [null, 'A', 'B', 'C'].includes(spec.diagram_label))) return false;
    if (chart.column_specs.some((spec) => spec.unit === null) && !chart.warnings.length) return false;
    if (!Array.isArray(chart.rows) || !chart.rows.length || !chart.rows.every((row) =>
      Array.isArray(row) && row.length === 2 && text(row[0]) && Array.isArray(row[1]) &&
      row[1].length === chart.columns.length && row[1].every((cell) => typeof cell === 'string'))) return false;
    const labels = new Set(chart.rows.map((row) => row[0]));
    if (labels.size !== chart.rows.length || !Array.isArray(chart.size_aliases)) return false;
    const aliases = new Map();
    return chart.size_aliases.every((alias) => {
      if (!exactKeys(alias, ['option_value', 'row_label', 'evidence']) || !text(alias.option_value) ||
        !text(alias.row_label) || !text(alias.evidence) || !labels.has(alias.row_label) ||
        aliases.has(alias.option_value) || (labels.has(alias.option_value) && alias.option_value !== alias.row_label)) return false;
      aliases.set(alias.option_value, alias.row_label);
      return true;
    });
  }

  function readImportedGuide(value) {
    if (!exactKeys(value, ['schema_version', 'size_option', 'audit', 'charts']) || value.schema_version !== 1 ||
      !(value.size_option === null || optionIdentity(value.size_option)) || !Array.isArray(value.charts)) return null;
    const audit = value.audit;
    if (!exactKeys(audit, ['run_id', 'source_fingerprint', 'record', 'reviewed_at', 'source_review_complete']) ||
      !text(audit.run_id) || !sha256(audit.source_fingerprint) || !text(audit.record) ||
      !text(audit.reviewed_at) || !/^\d{4}-\d{2}-\d{2}T.+(?:Z|[+-]\d{2}:\d{2})$/.test(audit.reviewed_at) ||
      !Number.isFinite(Date.parse(audit.reviewed_at)) || typeof audit.source_review_complete !== 'boolean') return null;
    const ids = new Set();
    const charts = [];
    value.charts.forEach((chart, index) => {
      if (!validChart(chart) || ids.has(chart.id)) return;
      ids.add(chart.id);
      charts.push({ index, chart, aliases: new Map(chart.size_aliases.map((alias) => [alias.option_value, alias.row_label])) });
    });
    return { sizeOption: value.size_option, charts };
  }

  const manualChartKeys = ['id', 'title', 'applicability', 'garment_type', 'columns', 'rows',
    'size_aliases', 'notes', 'warnings', 'diagram'];
  const quantities = ['chest', 'waist', 'hip', 'length', 'outer_length', 'inseam', 'sleeve', 'shoulders',
    'size_system', 'other', 'unknown'];
  const bases = ['flat_width', 'circumference', 'linear', 'body_measurement', 'size_system', 'unknown'];
  const diagramSegments = {
    tshirt: { A: 'chest|flat_width', B: 'length|linear', C: 'shoulders|linear' },
    longsleeve: { A: 'chest|flat_width', B: 'length|linear', C: 'sleeve|linear' },
    pants: { A: 'waist|flat_width', B: 'outer_length|linear', C: 'inseam|linear' },
    shorts: { A: 'waist|flat_width', B: 'outer_length|linear' },
    jacket: { A: 'chest|flat_width', B: 'length|linear', C: 'sleeve|linear' },
    vest: { A: 'chest|flat_width', B: 'length|linear' },
  };

  function validManualChart(chart, sizeOption, options) {
    if (!exactKeys(chart, manualChartKeys) || !text(chart.id) || !text(chart.title) ||
      !validApplicability(chart.applicability) || !(chart.garment_type === null || text(chart.garment_type)) ||
      typeof chart.notes !== 'string' || !strings(chart.warnings) ||
      !(chart.diagram === null || Object.hasOwn(diagramSegments, chart.diagram))) return false;
    if (chart.diagram !== null && (chart.diagram === 'longsleeve'
      ? !['longsleeve', 'hoodie'].includes(chart.garment_type) : chart.garment_type !== chart.diagram)) return false;
    if (chart.applicability.mode === 'option_values') {
      const positions = new Set();
      if (!chart.applicability.constraints.every((constraint) => {
        const option = options[constraint.position - 1];
        if (positions.has(constraint.position) || option?.name !== constraint.name ||
          !constraint.values.every((value) => option.values.includes(value))) return false;
        positions.add(constraint.position);
        return true;
      })) return false;
    }
    if (!Array.isArray(chart.columns) || !chart.columns.length || !chart.columns.every((column) =>
      exactKeys(column, ['key', 'label', 'quantity', 'basis', 'unit', 'diagram_label']) &&
      text(column.key) && text(column.label) && quantities.includes(column.quantity) && bases.includes(column.basis) &&
      (column.unit === null || text(column.unit)) && [null, 'A', 'B', 'C'].includes(column.diagram_label)) ||
      new Set(chart.columns.map((column) => column.key)).size !== chart.columns.length) return false;
    const segments = new Set();
    if (!chart.columns.every((column) => {
      if (column.diagram_label === null) return true;
      if (chart.diagram === null || segments.has(column.diagram_label) ||
        diagramSegments[chart.diagram][column.diagram_label] !== `${column.quantity}|${column.basis}`) return false;
      segments.add(column.diagram_label);
      return true;
    })) return false;
    if (!Array.isArray(chart.rows) || !chart.rows.length || !chart.rows.every((row) =>
      Array.isArray(row) && row.length === 2 && text(row[0]) && Array.isArray(row[1]) &&
      row[1].length === chart.columns.length && row[1].every((cell) => typeof cell === 'string'))) return false;
    const labels = new Set(chart.rows.map((row) => row[0]));
    if (labels.size !== chart.rows.length || !Array.isArray(chart.size_aliases)) return false;
    const aliases = new Set();
    return chart.size_aliases.every((alias) => {
      if (!Array.isArray(alias) || alias.length !== 2 || !alias.every(text) || !labels.has(alias[1]) ||
        !sizeOption || !options[sizeOption.position - 1].values.includes(alias[0]) ||
        aliases.has(alias[0]) || (labels.has(alias[0]) && alias[0] !== alias[1])) return false;
      aliases.add(alias[0]);
      return true;
    });
  }

  function readMerchantGuide(value, optionNames, options) {
    if (!exactKeys(value, ['schema_version', 'authoring', 'size_option', 'charts', 'history']) ||
      value.schema_version !== 2 || value.authoring !== 'merchant' ||
      !(value.size_option === null || optionIdentity(value.size_option)) ||
      !Array.isArray(value.charts) || !value.charts.length || !strings(optionNames) ||
      !Array.isArray(options) || options.length !== optionNames.length || !options.every((option, index) =>
        exactKeys(option, ['position', 'name', 'values']) && option.position === index + 1 &&
        option.name === optionNames[index] && strings(option.values) && option.values.length > 0 &&
        new Set(option.values).size === option.values.length) ||
      (value.size_option !== null && options[value.size_option.position - 1]?.name !== value.size_option.name)) return null;
    const ids = new Set();
    if (!value.charts.every((chart) => {
      if (!validManualChart(chart, value.size_option, options) || ids.has(chart.id)) return false;
      ids.add(chart.id);
      return true;
    })) return null;
    const history = value.history;
    if (!exactKeys(history, ['v1', 'copies']) || !(history.v1 === null || object(history.v1)) ||
      !Array.isArray(history.copies)) return null;
    const copied = new Set();
    if (!history.copies.every((entry) => {
      if (!exactKeys(entry, ['chart_id', 'product_id', 'source_chart_id']) || !ids.has(entry.chart_id) ||
        !/^gid:\/\/shopify\/Product\/\d+$/.test(entry.product_id) || !text(entry.source_chart_id) ||
        copied.has(entry.chart_id)) return false;
      copied.add(entry.chart_id);
      return true;
    })) return null;
    return { sizeOption: value.size_option, charts: value.charts.map((chart, index) =>
      ({ index, chart, aliases: new Map(chart.size_aliases) })) };
  }

  function readGuide(value, optionNames, options) {
    return value?.schema_version === 2
      ? readMerchantGuide(value, optionNames, options) : readImportedGuide(value);
  }

  class ProductSizeGuide extends HTMLElement {
    static openGuides = new Set();
    static nextToken = 0;

    connectedCallback() {
      this.hidden = true;
      this.owner = this.closest('product-info');
      this.dialog = this.querySelector('dialog');
      this.internalTrigger = this.querySelector('[data-size-guide-open]');
      this.trigger = this.internalTrigger;
      if (!this.owner || !this.dialog || !this.trigger || typeof this.dialog.showModal !== 'function') return;
      try {
        this.optionNames = JSON.parse(this.querySelector('[data-size-guide-options]').textContent);
        const optionValues = this.querySelector('[data-size-guide-option-values]');
        this.guide = readGuide(JSON.parse(this.querySelector('[data-size-guide-json]').textContent),
          this.optionNames, optionValues ? JSON.parse(optionValues.textContent) : null);
      } catch { return; }
      if (!this.guide?.charts.length || !strings(this.optionNames) || this.optionNames.length > 3) return;
      this.charts = this.guide.charts.flatMap((entry) => {
        const element = this.querySelector(`[data-size-guide-chart="${entry.index}"]`);
        return element ? [{ ...entry, element }] : [];
      });
      if (!this.charts.length) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.owner.addEventListener('click', (event) => {
        const opener = event.target.closest?.('[data-size-guide-open]');
        if (!opener || opener.closest('product-info') !== this.owner) return;
        this.syncTrigger();
        if (opener !== this.trigger || opener.hidden || this.hidden) return;
        this.dialog.classList.toggle('size-guide__dialog--motion', event.detail > 0);
        this.open();
      }, options);
      this.querySelector('[data-size-guide-close]').addEventListener('click', () => this.close(), options);
      this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); }, options);
      this.dialog.addEventListener('close', () => this.close(), options);
      this.dialog.addEventListener('click', (event) => {
        const rect = this.dialog.getBoundingClientRect();
        if (event.target === this.dialog &&
          (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)) this.close();
      }, options);
      this.owner.addEventListener('change', (event) => {
        if (event.target.closest('variant-selects')?.closest('product-info') === this.owner) this.refresh();
      }, options);
      window.addEventListener('popstate', () => {
        if (this.historyClosePending) {
          history.replaceState(history.state, '', this.historyClosePending.url);
        }
        this.historyClosePending = false;
        if (this.session && history.state?.milionSizeGuide !== this.session.token) this.close({ fromHistory: true });
      }, options);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data.sectionId !== (this.owner.dataset.originalSection || this.owner.dataset.section)) return;
          const source = data.html?.querySelector('product-info');
          if (source && source.dataset.productId !== this.dataset.productId) return;
          this.refresh();
        });
      }
      this.observer = new MutationObserver((records) => {
        if (records.some((record) => record.target === this.owner ||
          record.target.closest?.('variant-selects')?.closest('product-info') === this.owner ||
          [...record.addedNodes, ...record.removedNodes].some((node) => node.nodeType === 1 &&
            (node.matches('variant-selects') || node.querySelector('variant-selects'))))) this.refresh();
      });
      this.observer.observe(this.owner, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'data-product-id'] });
      this.scheduleTriggerLayout = () => {
        cancelAnimationFrame(this.triggerLayoutFrame);
        this.triggerLayoutFrame = requestAnimationFrame(() => {
          this.triggerLayoutFrame = null;
          if (this.isConnected && this.guide) this.syncTrigger();
        });
      };
      if (typeof ResizeObserver === 'function') this.resizeObserver = new ResizeObserver(this.scheduleTriggerLayout);
      window.addEventListener('resize', this.scheduleTriggerLayout, options);
      document.fonts?.addEventListener('loadingdone', this.scheduleTriggerLayout, options);
      document.fonts?.ready.then(() => {
        if (!this.events.signal.aborted) this.scheduleTriggerLayout();
      });
      this.refresh();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.events?.abort();
      this.unsubscribe?.();
      this.close({ disconnect: true });
      this.resizeObserver?.disconnect();
      this.observedSizeGroup = null;
      cancelAnimationFrame(this.triggerLayoutFrame);
      this.historyClosePending = false;
      this.guide = null;
    }

    currentPicker() {
      const pickers = [...this.owner.querySelectorAll('variant-selects')].filter((picker) =>
        picker.closest('product-info') === this.owner && picker.style.display !== 'none' && !picker.hidden);
      if (pickers.length !== 1 || pickers[0].dataset.section !== this.owner.dataset.section) return null;
      return pickers[0];
    }

    syncTrigger() {
      const buttons = [...this.owner.querySelectorAll('variant-selects [data-size-guide-open]')].filter((button) =>
        button.closest('product-info') === this.owner && button.getAttribute('aria-controls') === this.dialog.id);
      const identity = this.guide.sizeOption;
      const picker = this.currentPicker();
      const groups = identity && picker ? [...picker.querySelectorAll('[data-size-guide-option-position]')].filter((group) =>
        Number(group.dataset.sizeGuideOptionPosition) === identity.position && group.dataset.sizeGuideOptionName === identity.name) : [];
      const candidates = groups.length === 1 ? buttons.filter((button) => groups[0].contains(button)) : [];
      const external = candidates.length === 1 ? candidates[0] : null;
      this.trigger = external || this.internalTrigger;
      this.internalTrigger.hidden = external !== null;
      this.internalTrigger.setAttribute('aria-expanded', String(Boolean(this.session) && !external));
      buttons.forEach((button) => {
        button.hidden = this.hidden || button !== external;
        button.setAttribute('aria-expanded', String(Boolean(this.session) && button === external));
      });
      const group = external && !this.hidden && groups[0].matches('.product-form__input--pill') ? groups[0] : null;
      if (group !== this.observedSizeGroup) {
        this.resizeObserver?.disconnect();
        this.observedSizeGroup = group;
        if (group) this.resizeObserver?.observe(group);
      }
      if (!group) return;
      const heading = group.querySelector('legend.size-guide-option-heading');
      const row = heading?.querySelector('.size-guide-option-row');
      const labels = [...group.querySelectorAll('input[type="radio"] + label')]
        .map((label) => label.getBoundingClientRect()).filter((rect) => rect.width > 0);
      if (!row || !labels.length) return;
      const width = labels.at(-1).right - heading.getBoundingClientRect().left;
      const required = row.firstElementChild.getBoundingClientRect().width +
        external.firstElementChild.getBoundingClientRect().width + parseFloat(getComputedStyle(row).gap);
      const fits = width >= required && labels.every((rect) => Math.abs(rect.top - labels[0].top) <= 1);
      const destination = fits ? row : group;
      if (external.parentElement !== destination) destination.append(external);
      const headingWidth = fits ? `${width}px` : '';
      if (heading.style.getPropertyValue('--size-guide-heading-width') !== headingWidth) {
        if (fits) heading.style.setProperty('--size-guide-heading-width', headingWidth);
        else heading.style.removeProperty('--size-guide-heading-width');
      }
    }

    selection() {
      const picker = this.currentPicker();
      if (!picker) return null;
      const groups = [...picker.querySelectorAll('[data-size-guide-option-position]')];
      if (groups.length !== this.optionNames.length) return null;
      const selected = new Map();
      for (const group of groups) {
        const position = Number(group.dataset.sizeGuideOptionPosition);
        const name = group.dataset.sizeGuideOptionName;
        if (!Number.isInteger(position) || this.optionNames[position - 1] !== name || selected.has(position)) return null;
        const selects = group.querySelectorAll('select');
        const radios = group.querySelectorAll('input[type="radio"]:checked');
        let value;
        if (selects.length === 1 && !radios.length && selects[0].name === `options[${name}]`) {
          value = selects[0].value;
        } else if (!selects.length && radios.length === 1 && radios[0].name.trim() === `${name}-${position}`) {
          value = radios[0].value;
        } else return null;
        if (!text(value)) return null;
        selected.set(position, { name, value });
      }
      return selected;
    }

    refresh() {
      if (!this.guide) return;
      if (this.owner.dataset.productId !== this.dataset.productId || this.owner.dataset.section !== this.dataset.sectionId ||
        this.owner.style.display === 'none' || this.owner.hidden) {
        this.hidden = true;
        this.syncTrigger();
        this.querySelectorAll('tr.is-selected').forEach((row) => {
          row.classList.remove('is-selected');
          row.removeAttribute('aria-current');
          row.querySelector('[data-size-guide-current]').hidden = true;
        });
        this.close({ disconnect: true });
        return;
      }
      const selection = this.selection();
      const identity = this.guide.sizeOption;
      const size = identity && selection?.get(identity.position)?.name === identity.name
        ? selection.get(identity.position).value : null;
      let visibleCount = 0;
      let matched = false;
      for (const entry of this.charts) {
        const { chart, aliases, element } = entry;
        const applies = chart.applicability.mode === 'product' || chart.applicability.constraints.every((constraint) =>
          selection?.get(constraint.position)?.name === constraint.name &&
          constraint.values.includes(selection.get(constraint.position).value));
        element.hidden = !applies;
        if (applies) visibleCount++;
        const label = size === null ? null : aliases.get(size) ?? size;
        element.querySelectorAll('[data-size-guide-row]').forEach((row) => {
          const active = applies && label !== null && row.dataset.sizeGuideRow === label;
          row.classList.toggle('is-selected', active);
          row.querySelector('[data-size-guide-current]').hidden = !active;
          if (active) { row.setAttribute('aria-current', 'true'); matched = true; }
          else row.removeAttribute('aria-current');
        });
      }
      const status = this.querySelector('[data-size-guide-selection]');
      status.hidden = identity === null;
      status.textContent = identity === null
        ? '' : size === null
        ? 'Vybranou velikost nelze přiřadit k této tabulce.'
        : matched ? `Velikost ${size}` : `Velikost ${size} není v této tabulce uvedena.`;
      this.hidden = visibleCount === 0;
      this.syncTrigger();
      if (this.hidden) this.close();
      if (this.session) {
        this.session.url = location.href;
        this.keepHistoryMarker();
      }
    }

    keepHistoryMarker() {
      if (!this.session?.history || history.state?.milionSizeGuide === this.session.token) return;
      if (history.state?.milionSizeGuide) return;
      history.replaceState({ ...this.session.state, ...history.state, milionSizeGuide: this.session.token }, '', location.href);
    }

    open() {
      if (this.session || this.historyClosePending) return;
      this.refresh();
      if (this.hidden) return;
      const token = `size-guide-${++ProductSizeGuide.nextToken}`;
      this.session = { token, opener: this.trigger, state: history.state, url: location.href, history: false };
      this.dialog.showModal();
      try {
        history.pushState({ ...history.state, milionSizeGuide: token }, '', location.href);
        this.session.history = true;
      } catch { this.session.history = false; }
      this.trigger.setAttribute('aria-expanded', 'true');
      ProductSizeGuide.openGuides.add(this);
      document.documentElement.classList.add('size-guide-open');
      document.body.classList.add('size-guide-open');
      this.querySelector('[data-size-guide-close]').focus({ preventScroll: true });
    }

    close({ fromHistory = false, disconnect = false } = {}) {
      if (!fromHistory && !disconnect) this.keepHistoryMarker();
      const session = this.session;
      if (!session) return;
      this.session = null;
      if (this.dialog.open) this.dialog.close();
      this.syncTrigger();
      this.trigger.setAttribute('aria-expanded', 'false');
      ProductSizeGuide.openGuides.delete(this);
      if (!ProductSizeGuide.openGuides.size) {
        document.documentElement.classList.remove('size-guide-open');
        document.body.classList.remove('size-guide-open');
      }
      const opener = session.opener?.isConnected && !session.opener.hidden ? session.opener : this.trigger;
      if (!disconnect && !this.hidden && opener?.isConnected && !opener.hidden) opener.focus({ preventScroll: true });
      if (fromHistory && session.url !== location.href) history.replaceState(history.state, '', session.url);
      if (!fromHistory && session.history) {
        if (disconnect) {
          if (history.state?.milionSizeGuide === session.token) {
            const { milionSizeGuide, ...state } = history.state;
            history.replaceState(state, '', location.href);
          }
        } else if (history.state?.milionSizeGuide === session.token) {
          this.historyClosePending = { url: location.href };
          history.back();
        }
      }
    }
  }

  customElements.define('product-size-guide', ProductSizeGuide);
})();
